import { useFormik } from "formik";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import * as Yup from "yup";
import { useRegisterMutation } from "../features/auth/authApiSlice";
import { readStoredAttribution } from "../lib/attribution";

export const Register = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const emailFromUrl = searchParams.get("email");
  const referralId = searchParams.get("referral_code");
  const [email, setEmail] = useState<string>("");
  // hooks call
  const [register, { isError, isLoading }] = useRegisterMutation();

  const RegisterSchema = Yup.object().shape({
    name: Yup.string().max(60).required(),
    email: Yup.string().email("Invalid email").required("Email is required"),
    password: Yup.string().min(8).required("Please type a new password"),
    confirmPassword: Yup.string().oneOf(
      [Yup.ref("password"), null],
      "Passwords must match!"
    ),
    phone: Yup.string().required(),
    referralId: Yup.string().optional(),
    agreeToPolicy: Yup.boolean().isTrue(
      "You must agree to our privacy terms & policies."
    ),
    subscriptionConsent: Yup.boolean().optional(),
  });

  const registerHandler = async (
    name: string,
    email: string,
    password: string,
    phone: string,
    referralId?: string,
    subscriptionConsent?: boolean
  ) => {
    try {
      const userData: any = await register({
        name,
        email,
        password,
        phoneNumber: phone,
        referralId,
        ...(subscriptionConsent && { subscriptionConsent: true }),
        // First-touch attribution captured in main.tsx (may be undefined).
        ...(readStoredAttribution(window.localStorage, new Date()) ?? {}),
        signupPlatform: "web",
      }).unwrap();

      toast.success(`A verification code has been sent to ${email}`, {
        duration: 2000,
      });
      navigate(`/verify-email?email=${email}`);
    } catch (error: any) {
      console.log(error);
      toast.dismiss();
      toast.error(error?.message);
    }
  };

  const formik = useFormik({
    initialValues: {
      name: "",
      email: "",
      password: "",
      confirmPassword: "",
      phone: "",
      agreeToPolicy: false,
      referralId: referralId || "",
      subscriptionConsent: false,
    },
    validationSchema: RegisterSchema,
    onSubmit: async (values) => {
      registerHandler(
        values.name,
        values.email,
        values.password,
        values.phone,
        values.referralId || referralId || "",
        values.subscriptionConsent
      );
    },
  });

  useEffect(() => {
    if (emailFromUrl) {
      setEmail(emailFromUrl as string);
    }
  }, [emailFromUrl]);

  return (
    <div className="login-bg1 w-full font-dashboard bg-[#f8f8f8] flex justify-center items-center py-8">
      <div className="bg-white w-full max-w-[400px] rounded-md shadow-lg px-5 py-10">
        <h3 className="text-2xl text-indigo-500 font-semibold text-center">
          Gcbuying
        </h3>
        <h4 className="text-lg font-[500] text-slate-600 mt-8">
          Adventure starts here 🚀
        </h4>
        <p className="text-sm text-slate-500 mt-1">Register an account!</p>

        <form action="" className="mt-6" onSubmit={formik.handleSubmit}>
          <div className="flex flex-col gap-1">
            <label htmlFor="name" className="text-xs text-slate-600">
              Name
            </label>
            <input
              type="text"
              name="name"
              id="name"
              value={formik.values.name}
              onChange={formik.handleChange}
              placeholder="John Doe"
              className="transition-all duration-200 px-4 py-2 rounded-md text-sm border border-slate-300 focus:outline-none focus:ring-1 focus:ring-indigo-500"
            />

            <p className="text-red-500 text-xs">
              {formik.touched.name &&
                Boolean(formik.errors.name) &&
                formik.errors.name}
            </p>
          </div>
          <div className="flex flex-col gap-1 mt-4">
            <label htmlFor="email" className="text-xs text-slate-600">
              Email
            </label>
            <input
              type="email"
              name="email"
              id="email"
              value={formik.values.email}
              onChange={formik.handleChange}
              placeholder="john@example.com"
              className="transition-all duration-200 px-4 py-2 rounded-md text-sm border border-slate-300 focus:outline-none focus:ring-1 focus:ring-indigo-500"
            />

            <p className="text-red-500 text-xs">
              {formik.touched.email &&
                Boolean(formik.errors.email) &&
                formik.errors.email}
            </p>
          </div>
          <div className="flex flex-col gap-1 mt-4">
            <label htmlFor="password" className="text-xs text-slate-600">
              Password
            </label>

            <input
              type="text"
              name="password"
              id="password"
              value={formik.values.password}
              onChange={formik.handleChange}
              placeholder="••••••"
              className="w-full bg-transparent px-4 py-2 text-sm focus:outline-none border border-slate-300 rounded-md focus:ring-1 focus:ring-indigo-500"
            />

            <p className="text-red-500 text-xs">
              {formik.touched.password &&
                Boolean(formik.errors.password) &&
                formik.errors.password}
            </p>
          </div>

          <div className="flex flex-col gap-1 mt-4">
            <label htmlFor="confirmPassword" className="text-xs text-slate-600">
              Confirm Password
            </label>

            <input
              type="text"
              name="confirmPassword"
              id="confirmPassword"
              value={formik.values.confirmPassword}
              onChange={formik.handleChange}
              placeholder="••••••"
              className="w-full bg-transparent py-2 px-4 text-sm focus:outline-none border border-slate-300 rounded-md focus:ring-1 focus:ring-indigo-500"
            />

            <p className="text-red-500 text-xs">
              {formik.touched.confirmPassword &&
                Boolean(formik.errors.confirmPassword) &&
                formik.errors.confirmPassword}
            </p>
          </div>

          <div className="flex flex-col gap-1 mt-4">
            <label htmlFor="phone" className="text-xs text-slate-600">
              Phone Number (Include country code)
            </label>
            <input
              type="text"
              name="phone"
              id="phone"
              value={formik.values.phone}
              onChange={formik.handleChange}
              placeholder="eg +234XXXXXXXX"
              className="transition-all duration-200 px-4 py-2 rounded-md text-sm border border-slate-300 focus:outline-none focus:ring-1 focus:ring-indigo-500"
            />

            <p className="text-red-500 text-xs">
              {formik.touched.phone &&
                Boolean(formik.errors.phone) &&
                formik.errors.phone}
            </p>
          </div>

          <div className="flex flex-col gap-1 mt-4">
            <label htmlFor="referralId" className="text-xs text-slate-600">
              Referral Code (Optional)
            </label>
            <input
              type="text"
              name="referralId"
              id="referralId"
              value={formik.values.referralId}
              onChange={formik.handleChange}
              placeholder="eg ABC123"
              className="transition-all duration-200 px-4 py-2 rounded-md text-sm border border-slate-300 focus:outline-none focus:ring-1 focus:ring-indigo-500"
            />

            <p className="text-red-500 text-xs">
              {formik.touched.referralId &&
                Boolean(formik.errors.referralId) &&
                formik.errors.referralId}
            </p>
          </div>

          <div className="flex items-center gap-1 mt-3">
            <input
              type="checkbox"
              name="agreeToPolicy"
              id="agreeToPolicy"
              checked={formik.values.agreeToPolicy}
              onChange={formik.handleChange}
            />

            <label htmlFor="agreeToPolicy" className="text-sm text-slate-500">
              I agree to the{" "}
              <a
                target="_blank"
                href="https://gcbuying.com/terms"
                className="text-indigo-500 underline hover:text-indigo-500/60 transition-all duration-200"
              >
                Terms and Conditions{" "}
              </a>
              &{" "}
              <a
                target="_blank"
                href="https://gcbuying.com/privacy"
                className="text-indigo-500 underline hover:text-indigo-500/60 transition-all duration-200"
              >
                Privacy Policy
              </a>
            </label>
          </div>
          <p className="text-red-500 text-xs">
            {formik.touched.agreeToPolicy &&
              Boolean(formik.errors.agreeToPolicy) &&
              formik.errors.agreeToPolicy}
          </p>

          <div className="flex items-center gap-1 mt-3">
            <input
              type="checkbox"
              name="subscriptionConsent"
              id="subscriptionConsent"
              checked={formik.values.subscriptionConsent}
              onChange={formik.handleChange}
            />

            <label htmlFor="subscriptionConsent" className="text-sm text-slate-500">
              I'd like to receive updates, deals, and marketing emails from Gcbuying
            </label>
          </div>
          <p className="text-red-500 text-xs">
            {formik.touched.subscriptionConsent &&
              Boolean(formik.errors.subscriptionConsent) &&
              formik.errors.subscriptionConsent}
          </p>

          <button
            type="submit"
            className="mt-4 transition-all duration-200 w-full py-2 text-sm rounded-md text-white font-semibold bg-indigo-500 hover:brightness-105"
          >
            Sign up
          </button>

          <p className="text-sm text-center text-slate-500 mt-5">
            Already have an account?{" "}
            <Link to="/login" className="text-sm text-indigo-500">
              Sign in instead
            </Link>
          </p>
        </form>
      </div>
    </div>
  );
};
